import React, { useState, useRef, useEffect } from 'react';
import { RiZoomInLine, RiZoomOutLine, RiRefreshLine } from 'react-icons/ri';

const ImageCropperModal = ({ isOpen, imageSrc, onCropComplete, onClose }) => {
    const [zoom, setZoom] = useState(1);
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const imgRef = useRef(null);

    useEffect(() => {
        if (isOpen) {
            setZoom(1);
            setOffset({ x: 0, y: 0 });
        }
    }, [isOpen, imageSrc]);

    if (!isOpen || !imageSrc) return null;

    const handleMouseDown = (e) => {
        e.preventDefault();
        setIsDragging(true);
        setDragStart({ x: e.clientX - offset.x, y: e.clientY - offset.y });
    };

    const handleMouseMove = (e) => {
        if (!isDragging) return;
        setOffset({
            x: e.clientX - dragStart.x,
            y: e.clientY - dragStart.y
        });
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const handleTouchStart = (e) => {
        if (e.touches.length === 1) {
            setIsDragging(true);
            setDragStart({
                x: e.touches[0].clientX - offset.x,
                y: e.touches[0].clientY - offset.y
            });
        }
    };

    const handleTouchMove = (e) => {
        if (!isDragging || e.touches.length !== 1) return;
        setOffset({
            x: e.touches[0].clientX - dragStart.x,
            y: e.touches[0].clientY - dragStart.y
        });
    };

    const handleTouchEnd = () => {
        setIsDragging(false);
    };

    const handleCrop = () => {
        const img = imgRef.current;
        if (!img) return;

        const viewportSize = 300;
        const outputSize = 500;
        const scale = outputSize / viewportSize;

        const canvas = document.createElement('canvas');
        canvas.width = outputSize;
        canvas.height = outputSize;
        const ctx = canvas.getContext('2d');

        const naturalWidth = img.naturalWidth || img.width;
        const naturalHeight = img.naturalHeight || img.height;
        const aspectRatio = naturalWidth / naturalHeight;

        let baseWidth, baseHeight;
        if (aspectRatio > 1) {
            baseHeight = viewportSize;
            baseWidth = viewportSize * aspectRatio;
        } else {
            baseWidth = viewportSize;
            baseHeight = viewportSize / aspectRatio;
        }

        const scaledWidth = baseWidth * zoom * scale;
        const scaledHeight = baseHeight * zoom * scale;

        const posX = (outputSize / 2) - (scaledWidth / 2) + (offset.x * scale);
        const posY = (outputSize / 2) - (scaledHeight / 2) + (offset.y * scale);

        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, outputSize, outputSize);

        ctx.drawImage(img, posX, posY, scaledWidth, scaledHeight);

        canvas.toBlob((blob) => {
            if (blob) {
                const file = new File([blob], 'avatar.jpg', { type: 'image/jpeg', lastModified: Date.now() });
                const previewUrl = URL.createObjectURL(blob);
                onCropComplete(file, previewUrl);
            }
        }, 'image/jpeg', 0.88);
    };

    return (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.65)', zIndex: 1060 }} tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content shadow-lg border-0 rounded-4">
                    <div className="modal-header border-0 pb-0">
                        <h5 className="modal-title fw-bold">Crop & Resize Profile Picture</h5>
                        <button type="button" className="btn-close" onClick={onClose}></button>
                    </div>
                    <div className="modal-body text-center py-3">
                        <p className="text-muted fs-13 mb-3">
                            Drag image to adjust position • Use zoom slider to adjust scale
                        </p>

                        <div
                            className="position-relative mx-auto overflow-hidden border border-3 border-primary shadow-sm rounded-circle"
                            style={{
                                width: '300px',
                                height: '300px',
                                cursor: isDragging ? 'grabbing' : 'grab',
                                userSelect: 'none',
                                touchAction: 'none',
                                backgroundColor: '#f8f9fa'
                            }}
                            onMouseDown={handleMouseDown}
                            onMouseMove={handleMouseMove}
                            onMouseUp={handleMouseUp}
                            onMouseLeave={handleMouseUp}
                            onTouchStart={handleTouchStart}
                            onTouchMove={handleTouchMove}
                            onTouchEnd={handleTouchEnd}
                        >
                            <img
                                ref={imgRef}
                                src={imageSrc}
                                alt="Crop preview"
                                draggable={false}
                                style={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: `translate(-50%, -50%) translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
                                    maxHeight: '100%',
                                    maxWidth: '100%',
                                    objectFit: 'contain',
                                    pointerEvents: 'none',
                                    transition: isDragging ? 'none' : 'transform 0.1s ease-out'
                                }}
                            />
                        </div>

                        <div className="d-flex align-items-center justify-content-center gap-3 mt-4 px-4">
                            <RiZoomOutLine size={20} className="text-muted" />
                            <input
                                type="range"
                                className="form-range"
                                min="1"
                                max="3"
                                step="0.05"
                                value={zoom}
                                onChange={(e) => setZoom(parseFloat(e.target.value))}
                                style={{ maxWidth: '240px' }}
                            />
                            <RiZoomInLine size={20} className="text-muted" />
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary rounded-circle ms-2"
                                title="Reset position & zoom"
                                onClick={() => { setZoom(1); setOffset({ x: 0, y: 0 }); }}
                            >
                                <RiRefreshLine size={16} />
                            </button>
                        </div>
                    </div>

                    <div className="modal-footer border-0 pt-0">
                        <button type="button" className="btn btn-light px-4 rounded-pill" onClick={onClose}>
                            Cancel
                        </button>
                        <button type="button" className="btn btn-primary px-4 rounded-pill fw-bold" onClick={handleCrop}>
                            Crop & Apply
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ImageCropperModal;
